import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  Clock,
  Mail,
  ArrowRight,
  Send,
  CheckCircle,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [openingHours, setOpeningHours] = useState({});
  const [formData, setFormData] = useState({
    naam: "",
    email: "",
    telefoon: "",
    doelstelling: "",
    bericht: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const fetchOpeningHours = async () => {
      try {
        const response = await axios.get(`${API}/opening-hours`);
        setOpeningHours(response.data);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error("Error fetching opening hours:", err);
      }
    };
    fetchOpeningHours();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Bericht verzonden! We nemen snel contact op.");
      setFormData({
        naam: "",
        email: "",
        telefoon: "",
        doelstelling: "",
        bericht: "",
      });
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error("Error submitting form:", err);
      toast.error("Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const doelstellingen = [
    "Afvallen",
    "Spiermassa opbouwen",
    "Fitter worden",
    "Gezonder leven",
    "Sportprestaties verbeteren",
    "Anders",
  ];

  return (
    <main data-testid="contact-page" className="pt-20">
      {/* Hero Section */}
      <section className="section bg-[#0A0A0A]" data-testid="contact-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="caption mb-4 block">Contact</span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
              Neem Contact
              <br />
              <span className="text-red-500">Met Ons Op</span>
            </h1>
            <p className="text-zinc-400 text-lg">
              Heb je vragen of wil je meer informatie? Neem gerust contact met
              ons op. We helpen je graag verder.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="section bg-[#121212]" data-testid="contact-main">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-black text-white mb-8 font-['Barlow_Condensed'] uppercase">
                Contact <span className="text-red-500">Informatie</span>
              </h2>

              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Adres</h3>
                    <p className="text-zinc-400">
                      Oostendestraat 114, 8820 Torhout, Belgium
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Telefoon</h3>
                    <a
                      href="tel:0479380156"
                      data-testid="contact-phone"
                      className="text-zinc-400 hover:text-red-500 transition-colors"
                    >
                      0479 38 01 56
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">E-mail</h3>
                    <a
                      href="mailto:janvb@live.be"
                      data-testid="contact-email"
                      className="text-zinc-400 hover:text-red-500 transition-colors"
                    >
                      janvb@live.be
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-2">Openingsuren</h3>
                    <ul className="space-y-1">
                      {Object.entries(openingHours).map(([day, hours]) => (
                        <li
                          key={day}
                          className="flex justify-between text-sm text-zinc-400"
                        >
                          <span className="capitalize w-24">{day}</span>
                          <span>{hours}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Google Map */}
              <div
                className="map-container aspect-video"
                data-testid="contact-map"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2501.123456789!2d2.9654321!3d51.1565432!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTHCsDA5JzIzLjYiTiAywrA1Nyc1NS42IkU!5e0!3m2!1sen!2sbe!4v1234567890123!5m2!1sen!2sbe"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Arena Gym Location"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-black text-white mb-8 font-['Barlow_Condensed'] uppercase">
                Stuur Ons Een <span className="text-red-500">Bericht</span>
              </h2>

              {isSubmitted ? (
                <div className="bg-zinc-900/50 border border-red-500/30 p-8 rounded-sm text-center">
                  <CheckCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-black text-white mb-2 font-['Barlow_Condensed']">
                    BERICHT VERZONDEN!
                  </h3>
                  <p className="text-zinc-400 mb-6">
                    Bedankt voor je bericht. We nemen zo snel mogelijk contact
                    met je op.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="btn-secondary"
                    data-testid="send-another-btn"
                  >
                    Nog een bericht sturen
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                  data-testid="contact-form"
                >
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">
                      Naam *
                    </label>
                    <input
                      type="text"
                      name="naam"
                      value={formData.naam}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Je volledige naam"
                      data-testid="contact-naam-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="je@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">
                      Telefoon
                    </label>
                    <input
                      type="tel"
                      name="telefoon"
                      value={formData.telefoon}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="0479 12 34 56"
                      data-testid="contact-telefoon-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">
                      Doelstelling
                    </label>
                    <select
                      name="doelstelling"
                      value={formData.doelstelling}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="contact-doelstelling-select"
                    >
                      <option value="">Selecteer je doel</option>
                      {doelstellingen.map((doel) => (
                        <option key={doel} value={doel}>
                          {doel}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">
                      Bericht
                    </label>
                    <textarea
                      name="bericht"
                      value={formData.bericht}
                      onChange={handleChange}
                      rows={5}
                      className="form-input resize-none"
                      placeholder="Hoe kunnen we je helpen?"
                      data-testid="contact-bericht-textarea"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      "Verzenden..."
                    ) : (
                      <>
                        Verstuur Bericht
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-[#0A0A0A]" data-testid="contact-cta">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="caption mb-4 block">
              Klaar om je doelen te bereiken?
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
              Boek Je <span className="text-red-500">Intake</span>
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              Liever direct een afspraak maken? Boek je intake gesprek via onze
              online booking.
            </p>
            <Link
              to="/boeken"
              data-testid="contact-book-cta"
              className="btn-primary inline-flex items-center gap-2"
            >
              Boek Je Intake
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
